import { FC, useEffect, useMemo, useState } from "react";
import useRouteQueryParams from "../../../common/hooks/use-route-query-params";
import HeadingComponent from "../../../common/components/heading/heading.component";
import Row from "../../../common/components/grid/row";
import Column from "../../../common/components/grid/column";
import { OrderJoinRouteParams } from "../common/routes/types/order-join-route-params";
import ButtonComponent from "../../../common/components/button/button.component";
import OrderJoinOrderDetails from "./common/types/order-join-order-details";
import orderJoinOrderDetailsFactory from "./common/factory/order-join-order-details.factory";
import orderJoinHelper from "./order-join.helper";
import SearchRoadRoutingRequest from "../../../common/utils/search-road-route/search-road-routing.request";
import orderJoinOrderDetailsMapRouteFactory from "./common/factory/order-join-order-details-map-route.factory";
import MapRoute from "../../../common/components/map/types/map-route";
import SearchRoadRoutingResponse, {
  SearchRoadRoutingResponseRouteLeg,
} from "../../../common/utils/search-road-route/search-road-routing.response";
import MapMarker from "../../../common/components/map/types/map-marker";
import orderJoinOrderDetailsPlannedRouteMapMarkersFactory from "./common/factory/order-join-order-details-planned-route-map-markers.factory";
import { useAppContext } from "../../../context/app.context";
import orderBreadcrumbsHelper from "../common/breadcrumbs/order-breadcrumbs.helper";
import orderTranslationsHelper from "../../../languages/order-translations.helper";
import useDocumentTitle from "../../../common/hooks/use-document-title";
import appTranslationsHelper from "../../../languages/app-translations.helper";
import notificationService from "../../../common/utils/notification/notification.service";
import { useNavigate } from "react-router-dom";
import orderRoutesHelper from "../common/routes/order-routes.helper";
import orderJoinRouteParamsService, {
  OrderJoinOrderData,
} from "../common/routes/order-join-route-params.service";
import OrderJoinOrderDetailsComponent from "./common/details/order-join-order-details.component";
import searchRoadRoutingService from "../../../common/utils/search-road-route/search-road-routing.service";
import rideService from "../../../common/services/ride/ride.service";
import useAbort from "../../../common/hooks/use-abort";
import RideSolvedParams from "../../../common/services/ride/solved/ride-solved-params";
import { RideSolvedResponseRideSolvedWaypoint } from "../../../common/api/raily/ride/solved/ride-solved.response";
import orderJoinRideSolvedLoadParamsFactory from "./common/factory/order-join-ride-solved-load-params.factory";
import useOrderCargoOrderDetails from "../../../common/services/order/cargo-order/details/use-order-cargo-order-details";
import OrderCargoOrderDetailsParams from "../../../common/services/order/cargo-order/details/order-cargo-order-details-params";
import orderService from "../../../common/services/order/order.service";
import OrderJoinParams from "../../../common/services/order/join/order-join-params";
import OrderJoinError from "../../../common/services/order/join/order-join-error";
import { DataLoadErrorType } from "../../../common/utils/data-load-error/data-load-error";
import OrderJoin from "../../../common/services/order/join/order-join";
import useOrderJoin from "../../../common/services/order/join/use-order-join";
import orderJoinFactory from "./common/factory/order-join.factory";

type OrderJoinProps = {};

type FetchRouteResponse = {
  mapRoutes: MapRoute[];
  routeLegs: SearchRoadRoutingResponseRouteLeg[];
};

const OrderJoinComponent: FC<OrderJoinProps> = () => {
  const documentTitleTranslations =
    appTranslationsHelper.getDocumentTitleTranslations();

  useDocumentTitle(documentTitleTranslations.orderJoin);

  const navigate = useNavigate();

  const [routeQueryParams] = useRouteQueryParams<OrderJoinRouteParams>();

  const { setBreadcrumbs, selectedAppLanguage } = useAppContext();

  const [isMergeOrdersFetching, setIsMergeOrdersFetching] = useState(false);

  const [mergedOrderDetails, setMergedOrderDetails] =
    useState<OrderJoinOrderDetails | null>(null);

  const [sourceOrderDetails, setSourceOrderDetails] =
    useState<OrderJoinOrderDetails | null>(null);

  const [selectedSourceOrderUuid, setSelectedSourceOrderUuid] = useState("");

  const [sourceOrderDetailsResponse, setSourceOrderDetailsResponse] =
    useState<OrderJoin | null>(null);

  const [targetOrderDetailsResponse, setTargetOrderDetailsResponse] =
    useState<OrderJoin | null>(null);

  const [mergedOrderDetailsResponse, setMergedOrderDetailsResponse] =
    useState<OrderJoin | null>(null);

  const [sourceOrderMapRoutes, setSourceOrderMapRoutes] = useState<MapRoute[]>(
    [],
  );

  const [targetOrderMapRoutes, setTargetOrderMapRoutes] = useState<MapRoute[]>(
    [],
  );

  const [mergedOrderMapRoutes, setMergedOrderMapRoutes] = useState<MapRoute[]>(
    [],
  );

  const [targetOrderDetails, setTargetOrderDetails] =
    useState<OrderJoinOrderDetails | null>(null);

  const [sourceOrderSolvedRidesResponse, setSourceOrderSolvedRidesResponse] =
    useState<RideSolvedResponseRideSolvedWaypoint[]>([]);

  const [targetOrderSolvedRidesResponse, setTargetOrderSolvedRidesResponse] =
    useState<RideSolvedResponseRideSolvedWaypoint[]>([]);

  const [mergedOrderSolvedRidesResponse, setMergedOrderSolvedRidesResponse] =
    useState<RideSolvedResponseRideSolvedWaypoint[]>([]);

  const [sourceOrdersData, setSourceOrdersOptions] = useState<
    OrderJoinOrderData[]
  >([]);

  const cargoOrderTargetDetails = useOrderCargoOrderDetails();
  const cargoOrderSourceDetails = useOrderCargoOrderDetails();
  const cargoOrderMergedDetails = useOrderJoin();
  const cargoOrderTargetDetailsAbort = useAbort();
  const cargoOrderSourceDetailsAbort = useAbort();
  const cargoOrderMergedDetailsAbort = useAbort();
  const orderJoinAbort = useAbort();

  const mergedOrderDetailsFetchSuccessAbort = useAbort();
  const targetOrderDetailsFetchSuccessAbort = useAbort();
  const sourceOrdersDetailsFetchSuccessAbort = useAbort();

  useEffect(() => {
    const sourceOrdersData =
      orderJoinRouteParamsService.parse(routeQueryParams).sourceOrdersData;

    setSourceOrdersOptions(sourceOrdersData);

    setSelectedSourceOrderUuid(sourceOrdersData[0].uuid);
  }, [JSON.stringify(routeQueryParams.sourceOrders)]);

  const handleSourceOrderRouteResponse = (
    response: SearchRoadRoutingResponse,
  ) => {
    const mapRoutes = orderJoinOrderDetailsMapRouteFactory.createMapRoutes(
      response.routes,
    );

    setSourceOrderMapRoutes(mapRoutes);

    const routeLegs = response.routes.map((route) => route.legs).flat();

    const newSourceOrderDetails =
      orderJoinOrderDetailsFactory.createOrderDetails(
        sourceOrderDetailsResponse!,
        sourceOrderSolvedRidesResponse,
        routeLegs,
      );

    setSourceOrderDetails(newSourceOrderDetails);
  };

  const handleMergedOrderRouteResponse = (
    response: SearchRoadRoutingResponse,
  ) => {
    const mapRoutes = orderJoinOrderDetailsMapRouteFactory.createMapRoutes(
      response.routes,
    );

    setMergedOrderMapRoutes(mapRoutes);

    const routeLegs = response.routes.map((route) => route.legs).flat();

    const newMergedOrderDetails =
      orderJoinOrderDetailsFactory.createOrderDetails(
        mergedOrderDetailsResponse!,
        mergedOrderSolvedRidesResponse,
        routeLegs,
      );

    setMergedOrderDetails(newMergedOrderDetails);
  };

  const handleTargetOrderRouteResponse = (
    response: SearchRoadRoutingResponse,
  ) => {
    const mapRoutes = orderJoinOrderDetailsMapRouteFactory.createMapRoutes(
      response.routes,
    );

    setTargetOrderMapRoutes(mapRoutes);

    const routeLegs = response.routes.map((route) => route.legs).flat();

    const newTargetOrderDetails =
      orderJoinOrderDetailsFactory.createOrderDetails(
        targetOrderDetailsResponse!,
        targetOrderSolvedRidesResponse,
        routeLegs,
      );

    setTargetOrderDetails(newTargetOrderDetails);
  };

  const onMergedOrderDetailsFetchSuccess = async (response: OrderJoin) => {
    const isOrderSolved = orderJoinHelper.isOrderSolved(
      response.cargoOrder.transportingOrders,
    );

    if (isOrderSolved) {
      const orderDetails = orderJoinOrderDetailsFactory.createOrderDetails(
        response,
        null,
        [],
      );

      setMergedOrderDetails(orderDetails);
    } else {
      const loadParams: RideSolvedParams = {
        nodeExclusionCollection: { exclusions: [] },
        railyRide: orderJoinRideSolvedLoadParamsFactory.create(
          response.cargoOrder.ride,
        ),
      };

      rideService
        .fetchSolvedRides(
          loadParams,
          mergedOrderDetailsFetchSuccessAbort.signal,
        )
        .then((solvedRidesResponse) => {
          const orderDetails = orderJoinOrderDetailsFactory.createOrderDetails(
            response,
            solvedRidesResponse.seq,
            [],
          );

          setMergedOrderSolvedRidesResponse(solvedRidesResponse.seq);
          setMergedOrderDetails(orderDetails);
        });
    }

    setMergedOrderDetailsResponse(response);
  };

  const onTargetOrderDetailsFetchSuccess = async (response: OrderJoin) => {
    const isOrderSolved = orderJoinHelper.isOrderSolved(
      response.cargoOrder.transportingOrders,
    );

    if (isOrderSolved) {
      const orderDetails = orderJoinOrderDetailsFactory.createOrderDetails(
        response,
        null,
        [],
      );

      setTargetOrderDetails(orderDetails);
    } else {
      const loadParams: RideSolvedParams = {
        nodeExclusionCollection: { exclusions: [] },
        railyRide: orderJoinRideSolvedLoadParamsFactory.create(
          response.cargoOrder.ride,
        ),
      };

      rideService
        .fetchSolvedRides(
          loadParams,
          targetOrderDetailsFetchSuccessAbort.signal,
        )
        .then((solvedRidesResponse) => {
          const orderDetails = orderJoinOrderDetailsFactory.createOrderDetails(
            response,
            solvedRidesResponse.seq,
            [],
          );

          setTargetOrderSolvedRidesResponse(solvedRidesResponse.seq);
          setTargetOrderDetails(orderDetails);
        });
    }

    setTargetOrderDetailsResponse(response);
  };

  const onSourceOrdersDetailsFetchSuccess = (response: OrderJoin) => {
    const isOrderSolved = orderJoinHelper.isOrderSolved(
      response.cargoOrder.transportingOrders,
    );

    if (isOrderSolved) {
      const orderDetails = orderJoinOrderDetailsFactory.createOrderDetails(
        response,
        null,
        [],
      );

      setSourceOrderDetails(orderDetails);
    } else {
      const loadParams: RideSolvedParams = {
        nodeExclusionCollection: { exclusions: [] },
        railyRide: orderJoinRideSolvedLoadParamsFactory.create(
          response.cargoOrder.ride,
        ),
      };

      rideService
        .fetchSolvedRides(
          loadParams,
          sourceOrdersDetailsFetchSuccessAbort.signal,
        )
        .then((solvedRidesResponse) => {
          const orderDetails = orderJoinOrderDetailsFactory.createOrderDetails(
            response,
            solvedRidesResponse.seq,
            [],
          );

          setSourceOrderSolvedRidesResponse(solvedRidesResponse.seq);
          setSourceOrderDetails(orderDetails);
        });
    }

    setSourceOrderDetailsResponse(response);
  };

  const handleJoinOrdersError = (error: OrderJoinError) => {
    if (
      error.type !== DataLoadErrorType.OFFLINE &&
      error.type !== DataLoadErrorType.UNEXPECTED
    ) {
      notificationService.error(error.message);
    }
    onOrdersMergeFailure();
  };

  const onOrdersMergeSuccess = () => {
    const targetOrderUuid = routeQueryParams.targetOrderUuid;
    notificationService.success(translations.successMergeNotificationText);
    navigate(
      orderRoutesHelper.getListingOfActiveOrdersRoute({
        defaultSelectedOrderUuid: targetOrderUuid,
      }),
    );
  };

  const onOrdersMergeFailure = () => {
    notificationService.error(translations.failureMergeNotificationText);
  };

  const loadCargoOrderSourceDetails = (selectedSourceOrderUuid: string) => {
    const params: OrderCargoOrderDetailsParams = {
      orderUuid: selectedSourceOrderUuid,
    };
    cargoOrderSourceDetails.load(params, cargoOrderSourceDetailsAbort.signal);
  };

  const loadCargoOrderTargetDetails = (targetOrderUuid: string) => {
    const params: OrderCargoOrderDetailsParams = {
      orderUuid: targetOrderUuid,
    };
    cargoOrderTargetDetails.load(params, cargoOrderTargetDetailsAbort.signal);
  };

  const fetchPreview = () => {
    const params: OrderJoinParams = {
      dryRun: true,
      sourceOrderIds: sourceOrdersData!.map((order) => order.uuid),
      targetOrderUuid: routeQueryParams.targetOrderUuid,
    };

    return cargoOrderMergedDetails.load(
      params,
      cargoOrderMergedDetailsAbort.signal,
    );
  };

  useEffect(() => {
    if (!cargoOrderSourceDetails.data) {
      return;
    }

    const data = orderJoinFactory.create(cargoOrderSourceDetails.data);

    onSourceOrdersDetailsFetchSuccess(data);
  }, [cargoOrderSourceDetails.data]);

  useEffect(() => {
    if (!cargoOrderTargetDetails.data) {
      return;
    }

    const data = orderJoinFactory.create(cargoOrderTargetDetails.data);

    onTargetOrderDetailsFetchSuccess(data);
  }, [cargoOrderTargetDetails.data]);

  useEffect(() => {
    if (!cargoOrderMergedDetails.data) {
      return;
    }
    onMergedOrderDetailsFetchSuccess(cargoOrderMergedDetails.data);
  }, [cargoOrderMergedDetails.data]);

  useEffect(() => {
    if (sourceOrderDetails) {
      const fetchRouteResult = fetchRoute(
        sourceOrderDetails,
        handleSourceOrderRouteResponse,
      );

      setSourceOrderMapRoutes(fetchRouteResult?.mapRoutes ?? []);

      const newSourceOrder = orderJoinOrderDetailsFactory.createOrderDetails(
        sourceOrderDetailsResponse!,
        sourceOrderSolvedRidesResponse,
        fetchRouteResult?.routeLegs ?? [],
      );

      setSourceOrderDetails(newSourceOrder);
    }
  }, [sourceOrderDetails?.orderId]);

  useEffect(() => {
    if (mergedOrderDetails) {
      const fetchRouteResult = fetchRoute(
        mergedOrderDetails,
        handleMergedOrderRouteResponse,
      );

      setMergedOrderMapRoutes(fetchRouteResult?.mapRoutes ?? []);

      const newSourceOrder = orderJoinOrderDetailsFactory.createOrderDetails(
        mergedOrderDetailsResponse!,
        mergedOrderSolvedRidesResponse,
        fetchRouteResult?.routeLegs ?? [],
      );

      setMergedOrderDetails(newSourceOrder);
    }
  }, [mergedOrderDetails?.orderId]);

  useEffect(() => {
    if (targetOrderDetails) {
      const fetchRouteResult = fetchRoute(
        targetOrderDetails,
        handleTargetOrderRouteResponse,
      );

      setTargetOrderMapRoutes(fetchRouteResult?.mapRoutes ?? []);

      const newTargetOrder = orderJoinOrderDetailsFactory.createOrderDetails(
        targetOrderDetailsResponse!,
        targetOrderSolvedRidesResponse,
        fetchRouteResult?.routeLegs ?? [],
      );

      setTargetOrderDetails(newTargetOrder);
    }
  }, [targetOrderDetails?.orderId]);

  useEffect(() => {
    if (!selectedSourceOrderUuid) {
      return;
    }

    loadCargoOrderSourceDetails(selectedSourceOrderUuid);
    return cargoOrderSourceDetailsAbort.revoke;
  }, [selectedSourceOrderUuid]);

  useEffect(() => {
    if (!routeQueryParams.targetOrderUuid) {
      return;
    }

    loadCargoOrderTargetDetails(routeQueryParams.targetOrderUuid);
    return cargoOrderTargetDetailsAbort.revoke;
  }, [routeQueryParams.targetOrderUuid]);

  useEffect(() => {
    if (!routeQueryParams.targetOrderUuid || !sourceOrdersData.length) {
      return;
    }

    fetchPreview();
  }, [routeQueryParams.targetOrderUuid, sourceOrdersData]);

  useEffect(() => {
    const breadcrumbs = orderBreadcrumbsHelper.getOrderJoinBreadcrumbs({
      targetOrderUuid: routeQueryParams.targetOrderUuid,
      sourceOrders: routeQueryParams.sourceOrders,
    });

    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage]);

  const sourceOrderMapMarkers: MapMarker[] = useMemo(() => {
    if (sourceOrderDetails?.routeWaypoints) {
      return orderJoinOrderDetailsPlannedRouteMapMarkersFactory.createMapMarkers(
        sourceOrderDetails?.routeWaypoints,
      );
    }

    return [];
  }, [sourceOrderDetails?.routeWaypoints]);

  const targetOrderMapMarkers: MapMarker[] = useMemo(() => {
    if (targetOrderDetails?.routeWaypoints) {
      return orderJoinOrderDetailsPlannedRouteMapMarkersFactory.createMapMarkers(
        targetOrderDetails?.routeWaypoints,
      );
    }

    return [];
  }, [targetOrderDetails?.routeWaypoints]);

  const mergedOrderMapMarkers: MapMarker[] = useMemo(() => {
    if (mergedOrderDetails?.routeWaypoints) {
      return orderJoinOrderDetailsPlannedRouteMapMarkersFactory.createMapMarkers(
        mergedOrderDetails?.routeWaypoints,
      );
    }

    return [];
  }, [mergedOrderDetails?.routeWaypoints]);

  const fetchRoute = (
    orderDetails: OrderJoinOrderDetails,
    handleRouteResponse: (response: any) => void,
  ): FetchRouteResponse | null => {
    const isRouteSequenceSolved = orderJoinHelper.isRouteSequenceSolved(
      orderDetails.routeWaypoints,
    );

    if (isRouteSequenceSolved) {
      fetchRouteForSolvedOrder(orderDetails, handleRouteResponse);

      return null;
    }

    const waypointsCouldBeConnected =
      orderJoinHelper.getWaypointGroupsCouldBeConnected(
        orderDetails.routeWaypoints,
      );

    const coordinatesOfWaypointsCouldBeConnected =
      orderJoinHelper.getCoordinatesOfWaypointsCouldBeConnected(
        waypointsCouldBeConnected,
      );

    const fetchPromises: Promise<SearchRoadRoutingResponse>[] = [];

    coordinatesOfWaypointsCouldBeConnected.forEach((coordinates) => {
      const request: SearchRoadRoutingRequest = {
        waypointCoordinates: coordinates,
        excludeHighway: orderDetails.nodeExclusion.excludeInside,
      };

      const fetch = searchRoadRoutingService.search(request);

      fetchPromises.push(fetch);
    });

    let result: FetchRouteResponse | null = null;

    Promise.all(fetchPromises).then((responses) => {
      const mapRouteWaypointGroups: MapRoute["waypoints"][] = [];

      responses.forEach((response) => {
        const mapRoute = response.routes[0]
          ? orderJoinOrderDetailsMapRouteFactory.createMapRoute(
              response.routes[0].geometry.coordinates,
            )
          : null;

        if (mapRoute?.waypoints) {
          mapRouteWaypointGroups.push(mapRoute.waypoints);
        }
      });

      const newMapRoutes: MapRoute[] = mapRouteWaypointGroups.map(
        (routeWaypointGroup) => ({
          waypoints: routeWaypointGroup,
        }),
      );
      const responseRouteLegs = responses
        .map((response) => response.routes.map((route) => route.legs).flat())
        .flat();

      result = { mapRoutes: newMapRoutes, routeLegs: responseRouteLegs };
    });

    return result;
  };

  const fetchRouteForSolvedOrder = (
    orderDetails: OrderJoinOrderDetails,
    handleRouteResponse: (response: any) => void,
  ) => {
    const request: SearchRoadRoutingRequest = {
      waypointCoordinates: orderDetails.routeWaypoints.map((waypoint) => ({
        latitude: waypoint.place.latitude,
        longitude: waypoint.place.longitude,
      })),
      excludeHighway: orderDetails.nodeExclusion.excludeInside,
    };

    searchRoadRoutingService.search(request).then(handleRouteResponse);
  };

  const translations = orderTranslationsHelper.getOrderJoinTranslations();

  const joinOrders = () => {
    const params: OrderJoinParams = {
      dryRun: false,
      sourceOrderIds: sourceOrdersData.map((order) => order.uuid),
      targetOrderUuid: routeQueryParams.targetOrderUuid,
    };

    setIsMergeOrdersFetching(true);

    orderService
      .joinOrder(params, orderJoinAbort.signal)
      .then(onOrdersMergeSuccess)
      .catch(handleJoinOrdersError)
      .finally(() => setIsMergeOrdersFetching(false));
  };

  const onSubmitButtonClick = () => {
    joinOrders();
  };

  const SourceOrderSelect = (
    <div className="order_join_select_order_button_wrapper">
      {sourceOrdersData.map((order) => (
        <ButtonComponent
          type={order.uuid === selectedSourceOrderUuid ? "success" : undefined}
          classNames={{ root: "mt-1" }}
          onClick={() => {
            setSelectedSourceOrderUuid(order.uuid);
          }}
          key={order.uuid}
        >
          {order.internalOrderId}
        </ButtonComponent>
      ))}
    </div>
  );

  return (
    <div className="order_join">
      <HeadingComponent
        title={translations.header.headingText}
        actions={[
          <ButtonComponent
            onClick={onSubmitButtonClick}
            type="primary"
            isLoading={isMergeOrdersFetching}
            idForTesting={`order-join-submit-button`}
            title={translations.submitButtonTitle}
            isDisabled={
              cargoOrderMergedDetails.isError ||
              cargoOrderMergedDetails.isLoading
            }
          >
            {translations.submitButtonText}
          </ButtonComponent>,
        ]}
      />
      <Row>
        <Column xl={4} withPaddings>
          <OrderJoinOrderDetailsComponent
            details={targetOrderDetails}
            headingText={translations.targetOrder.headingText}
            isError={cargoOrderTargetDetails.isError}
            isLoading={cargoOrderTargetDetails.isLoading}
            mapMarkers={targetOrderMapMarkers}
            mapRoutes={targetOrderMapRoutes}
            errorMessage={
              translations.targetOrder.failureDetailsFetchNotificationText
            }
          />
        </Column>
        <Column xl={4} withPaddings>
          <OrderJoinOrderDetailsComponent
            details={mergedOrderDetails}
            headingText={translations.joinPreview.headingText}
            isError={cargoOrderMergedDetails.isError}
            isLoading={cargoOrderMergedDetails.isLoading}
            mapMarkers={mergedOrderMapMarkers}
            mapRoutes={mergedOrderMapRoutes}
            errorMessage={translations.joinPreview.failureFetchNotificationText}
          />
        </Column>
        <Column xl={4} withPaddings>
          <OrderJoinOrderDetailsComponent
            details={sourceOrderDetails}
            headingText={translations.sourceOrder.headingText}
            isError={cargoOrderSourceDetails.isError}
            isLoading={cargoOrderSourceDetails.isLoading}
            mapMarkers={sourceOrderMapMarkers}
            mapRoutes={sourceOrderMapRoutes}
            errorMessage={
              translations.sourceOrder.failureDetailsFetchNotificationText
            }
            selector={SourceOrderSelect}
          />
        </Column>
      </Row>
    </div>
  );
};

export default OrderJoinComponent;
